import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { chain, sumBy, every } from 'lodash';
import { Row, Col, Button, Stack, Card, Modal, ModalProps } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Form, Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faDownload } from '@fortawesome/free-solid-svg-icons';
import { HealthClaimCostItem, HealthClaimCostItemStatuses } from '@models/HealthClaimCostItem';
import { ClaimStatuses } from '@models/InsuranceClaim';
import { NumericString } from '@utils/typescript';
import { selectHealthClaim } from './selector';
import { object, string } from 'yup';
import { FormGroupControl, NoData, ConfirmModal } from '@components/Base';
import { healthClaimApprove, HealthClaimApprovePayload, healthClaimSettlementLetterNotesUpdate } from './action';
import { HealthClaimSettlementLetterNotesUpdatePayload } from './action';

type SettlementLetterNotesModalProps = ModalProps & {
  healthClaimId: number;
  settlementLetterNotes: string | '';
  onSubmit: (values: { settlementLetterNotes: string | '' }) => void;
};

const SettlementLetterNotesModal = ({
  healthClaimId,
  settlementLetterNotes,
  onSubmit,
  ...props
}: SettlementLetterNotesModalProps) => {
  const { t } = useTranslation();
  const initialValues = { settlementLetterNotes: settlementLetterNotes || '' };
  const validationSchema = object({
    settlementLetterNotes: string().optional(),
  });

  return (
    <Modal {...props} centered size="lg">
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
        <Form>
          <Modal.Header closeButton>
            <Modal.Title>Notes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col>
                <FormGroupControl
                  as="textarea"
                  rows={5}
                  name="settlementLetterNotes"
                  groupProps={{ className: 'mb-3' }}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={props.onHide}>
              {t('confirmModal.cancel')}
            </Button>
            <Button type="submit" variant="primary">
              {t('common:submit')}
            </Button>
          </Modal.Footer>
        </Form>
      </Formik>
    </Modal>
  );
};

type TotalAmountsProps = {
  totalDeclaredAmount: NumericString;
  totalUncoveredExpensesAmount: NumericString;
  totalDeductibleCopayAmount: NumericString;
  totalOverLimitAmount: NumericString;
  totalCoveredAmount: NumericString;
};

const TotalAmounts = ({
  totalDeclaredAmount,
  totalUncoveredExpensesAmount,
  totalDeductibleCopayAmount,
  totalOverLimitAmount,
  totalCoveredAmount,
}: TotalAmountsProps) => {
  return (
    <Stack direction="horizontal" gap={5} className="mt-5">
      <Stack>
        <span>Total amount declared:</span>
        <h5>{Number(totalDeclaredAmount).toFixed(2)}</h5>
      </Stack>
      <Stack>
        <span>Total expenses not covered:</span>
        <h5>{Number(totalUncoveredExpensesAmount).toFixed(2)}</h5>
      </Stack>
      <Stack>
        <span>Total deductible or co-pay amount:</span>
        <h5>{Number(totalDeductibleCopayAmount).toFixed(2)}</h5>
      </Stack>
      <Stack>
        <span>Total over limit amount:</span>
        <h5>{Number(totalOverLimitAmount).toFixed(2)}</h5>
      </Stack>
      <Stack>
        <span>Total amount covered:</span>
        <h5>{Number(totalCoveredAmount).toFixed(2)}</h5>
      </Stack>
    </Stack>
  );
};

const SettlementApprovalTabContent = () => {
  const dispatch = useDispatch();
  const { healthClaim, healthClaimCostItems } = useSelector(selectHealthClaim);
  const [showSettlementLetterNotesModal, setShowSettlementLetterNotesModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const GroupedHealthClaimsCostItems = chain(healthClaimCostItems)
    .groupBy((item) => item.benefitLimit?.id ?? item.customSubBenefit)
    .map((items) => {
      return {
        ...items[0],
        declaredAmount: sumBy(items, (item) => Number(item.declaredAmount)),
        uncoveredExpensesAmount: sumBy(items, (item) => Number(item.uncoveredExpensesAmount)),
        deductibleCopayAmount: sumBy(items, (item) => Number(item.deductibleCopayAmount)),
        overLimitAmount: sumBy(items, (item) => Number(item.overLimitAmount)),
        totalCoveredAmount: sumBy(items, (item) => Number(item.totalCoveredAmount)),
      } as unknown as HealthClaimCostItem;
    })
    .value();

  const totalDeclaredAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.declaredAmount),
    0,
  );
  const totalUncoveredExpensesAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.uncoveredExpensesAmount),
    0,
  );
  const totalDeductibleCopayAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.deductibleCopayAmount),
    0,
  );
  const totalOverLimitAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.overLimitAmount),
    0,
  );
  const totalCoveredAmount = (healthClaimCostItems ?? []).reduce(
    (total, item) => total + Number(item.totalCoveredAmount),
    0,
  );

  const columns = useMemo(
    () =>
      [
        {
          name: 'Sub benfit',
          selector: (row) => (row.benefitLimit ? row.benefitLimit.subBenefit : row.customSubBenefit),
          width: '300px',
          wrap: true,
        },
        {
          name: 'Amount declared',
          selector: (row) => Number(row.declaredAmount).toFixed(2),
          width: '200px',
          wrap: true,
        },
        {
          name: 'Expenses not covered',
          selector: (row) => Number(row.uncoveredExpensesAmount).toFixed(2),
          width: '200px',
        },
        {
          name: 'Deductible or co-pay amount',
          selector: (row) => Number(row.deductibleCopayAmount).toFixed(2),
          width: '300px',
        },
        {
          name: 'Over limit amount',
          selector: (row) => Number(row.overLimitAmount).toFixed(2),
          width: '200px',
        },
        {
          name: 'Total amount covered',
          selector: (row) => Number(row.totalCoveredAmount).toFixed(2),
          width: '200px',
        },
      ] as TableColumn<HealthClaimCostItem>[],
    [],
  );

  const customStyles = {
    headCells: {
      style: {
        fontWeight: 'bold',
      },
    },
  };

  const shouldDisplayApproveBtn =
    (healthClaimCostItems && healthClaimCostItems.length > 0
      ? every(healthClaimCostItems, { status: HealthClaimCostItemStatuses.Approved })
      : false) && healthClaim?.status !== ClaimStatuses.Approved;

  return (
    <>
      <div className="d-flex flex-row justify-content-between mb-5">
        <h5>Costs Summary</h5>
        <Stack gap={2} className="flex-grow-0">
          <Stack direction="horizontal" gap={2} className="justify-content-end">
            {shouldDisplayApproveBtn && (
              <Button
                variant="success"
                onClick={() => {
                  setShowConfirmModal(true);
                }}
              >
                <FontAwesomeIcon icon={faCheck} className="fa-fw me-2" />
                Approve
              </Button>
            )}

            {healthClaim && healthClaim.settlementLetter && (
              <Button href={healthClaim.settlementLetter} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faDownload} className="fa-fw me-2" />
                Settlement Letter EN
              </Button>
            )}

            {healthClaim && healthClaim.settlementLetterKm && (
              <Button href={healthClaim.settlementLetterKm} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faDownload} className="fa-fw me-2" />
                Settlement Letter KM
              </Button>
            )}
          </Stack>
          <Stack direction="horizontal" gap={2} className="justify-content-end">
            {healthClaim && healthClaim.internalSettlementLetter && (
              <Button
                variant="outline-primary"
                href={healthClaim.internalSettlementLetter}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faDownload} className="fa-fw me-2" />
                Internal Settlement Letter EN
              </Button>
            )}

            {healthClaim && healthClaim.internalSettlementLetterKm && (
              <Button
                variant="outline-primary"
                href={healthClaim.internalSettlementLetterKm}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faDownload} className="fa-fw me-2" />
                Internal Settlement Letter KM
              </Button>
            )}
          </Stack>
        </Stack>
      </div>

      <DataTable columns={columns} data={GroupedHealthClaimsCostItems ?? []} customStyles={customStyles} fixedHeader />

      <TotalAmounts
        totalDeclaredAmount={`${totalDeclaredAmount}`}
        totalUncoveredExpensesAmount={`${totalUncoveredExpensesAmount}`}
        totalDeductibleCopayAmount={`${totalDeductibleCopayAmount}`}
        totalOverLimitAmount={`${totalOverLimitAmount}`}
        totalCoveredAmount={`${totalCoveredAmount}`}
      />

      {healthClaim && (
        <>
          <Card className="mt-5">
            <Card.Header>
              <Stack direction="horizontal" gap={2} className="align-items-center">
                <h5 className="mb-0 me-auto">Notes</h5>
                <Button size="sm" onClick={() => setShowSettlementLetterNotesModal(true)}>
                  {healthClaim.settlementLetterNotes ? 'Edit' : 'Add'}
                </Button>
              </Stack>
            </Card.Header>
            <Card.Body style={{ whiteSpace: 'pre-line' }}>{healthClaim.settlementLetterNotes || <NoData />}</Card.Body>
          </Card>

          <SettlementLetterNotesModal
            healthClaimId={healthClaim.id}
            settlementLetterNotes={String(healthClaim.settlementLetterNotes)}
            show={showSettlementLetterNotesModal}
            onHide={() => setShowSettlementLetterNotesModal(false)}
            onSubmit={(values: { settlementLetterNotes: string | '' }) => {
              const payload: HealthClaimSettlementLetterNotesUpdatePayload = {
                id: healthClaim.id,
                settlementLetterNotes: values.settlementLetterNotes,
              };
              dispatch(healthClaimSettlementLetterNotesUpdate(payload));
              setShowSettlementLetterNotesModal(false);
            }}
          />
        </>
      )}

      {healthClaim && (
        <ConfirmModal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          onConfirm={() => {
            const payload: HealthClaimApprovePayload = {
              id: healthClaim.id,
            };
            dispatch(healthClaimApprove(payload));
            setShowConfirmModal(false);
          }}
        />
      )}
    </>
  );
};

export { SettlementApprovalTabContent };
